import React, { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { limit, query } from "firebase/firestore";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { PRODUCTS } from "../../config";
import { compareBy, quotaEntries } from "../../lib/licenses";
import {
  distributorKeyCountQuery,
  distributorKeysQuery,
} from "../../lib/queries";
import { useCounts } from "../../hooks/usePaged";
import { useLiveQuery } from "../../hooks/useLive";
import { figureSx } from "../../theme";
import {
  EmptyState,
  Loading,
  PageHeader,
  QuotaMeter,
  SectionTitle,
} from "../../components/ui";
import IssueKeyDialog from "../../components/IssueKeyDialog";
import LicenseDialog from "../../components/LicenseDialog";
import LicenseTable from "../../components/LicenseTable";
import { useDistributorData } from "./DistributorData";

function QuotaTile({ entry, disabled, onIssue }) {
  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle2">{entry.productName}</Typography>
        <Typography variant="caption" color="text.secondary">
          {entry.type} licence
        </Typography>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, my: 1.5 }}>
          <Typography sx={{ ...figureSx, fontSize: "2.5rem" }}>
            {entry.remaining}
          </Typography>
          <Typography color="text.secondary">keys left</Typography>
        </Box>
        <QuotaMeter allocated={entry.allocated} issued={entry.issued} />
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ mt: 2 }}
          disabled={disabled || entry.remaining === 0}
          onClick={() => onIssue(entry.bucket)}
        >
          Issue key
        </Button>
      </CardContent>
    </Card>
  );
}

export default function DistributorHome() {
  const { distributorId, distributor, distributorLoading, error } =
    useDistributorData();
  const [issuing, setIssuing] = useState(null); // null | bucket ("" = any)
  const [openKey, setOpenKey] = useState(null);

  // The 5 most recently issued keys across both products, and the totals.
  const [first, second] = PRODUCTS;
  const recentA = useLiveQuery(
    `recent/${first.id}/${distributorId}`,
    () => query(distributorKeysQuery(first.id, distributorId), limit(5)),
    { product: first.id },
  );
  const recentB = useLiveQuery(
    `recent/${second.id}/${distributorId}`,
    () => query(distributorKeysQuery(second.id, distributorId), limit(5)),
    { product: second.id },
  );
  const recent = useMemo(
    () =>
      [...recentA.data, ...recentB.data]
        .sort(compareBy("issuedAt", "desc"))
        .slice(0, 5),
    [recentA.data, recentB.data],
  );
  const totals = useCounts(
    `home/${distributorId}/${recent.map((k) => k.id).join()}`,
    Object.fromEntries(
      PRODUCTS.map((p) => [
        p.id,
        () => distributorKeyCountQuery(p.id, distributorId),
      ]),
    ),
  );
  const totalKeys = PRODUCTS.reduce(
    (sum, p) => sum + (totals.counts[p.id] || 0),
    0,
  );

  if (distributorLoading) return <Loading />;
  if (!distributor) {
    return (
      <Alert severity="error">
        We couldn't load your distributor account
        {error ? ` (${error.message})` : ""}. Please contact LiFE.
      </Alert>
    );
  }

  const paused = distributor.active === false;
  const entries = quotaEntries(distributor);
  const canIssue = !paused && entries.some((e) => e.remaining > 0);
  const openLicense = openKey
    ? recent.find((k) => `${k.product}/${k.id}` === openKey)
    : null;

  return (
    <>
      <PageHeader
        eyebrow={distributor.region}
        title={`Welcome, ${distributor.name}`}
        subtitle="Issue a licence key for each customer. A new key is created when you issue it and counts against your quota."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!canIssue}
            onClick={() => setIssuing("")}
          >
            Issue key
          </Button>
        }
      />

      {paused && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your account is paused, so new keys can't be issued. Keys you already
          issued keep working.
        </Alert>
      )}

      {entries.length === 0 ? (
        <EmptyState
          title="No keys allocated yet"
          body="Once LiFE allocates keys to your account they'll show up here, ready to issue to your customers."
        />
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          }}
        >
          {entries.map((entry) => (
            <QuotaTile
              key={entry.bucket}
              entry={entry}
              disabled={paused}
              onIssue={setIssuing}
            />
          ))}
        </Box>
      )}

      <SectionTitle
        action={
          totalKeys > 5 && (
            <Button component={RouterLink} to="/keys" size="small">
              All {totalKeys.toLocaleString()} keys
            </Button>
          )
        }
      >
        Recently issued
      </SectionTitle>
      {recentA.loading || recentB.loading ? (
        <Loading />
      ) : (
        <LicenseTable
          rows={recent}
          columns={["key", "status", "productType", "customer", "issued"]}
          onRowClick={(l) => setOpenKey(`${l.product}/${l.id}`)}
          emptyText="You haven't issued any keys yet."
        />
      )}

      <IssueKeyDialog
        open={issuing !== null}
        initialBucket={issuing || undefined}
        distributor={distributor}
        onClose={() => setIssuing(null)}
      />
      {openLicense && (
        <LicenseDialog license={openLicense} onClose={() => setOpenKey(null)} />
      )}
    </>
  );
}
